import { Fragment, useEffect, useMemo, useState } from 'react';
import Modal from '../../../shared/components/modals/Modal';
import { formatCurrencyDynamic } from '../../../utils/numberUtils';

const NUMERIC_COLUMNS = new Set(['pending_count', 'total_value', 'oldest_days']);

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const Count = ({ value, tone }) => {
  const n = toNumber(value);
  const className = n === 0
    ? 'sa-coverage-modal__count sa-coverage-modal__count--zero'
    : `sa-coverage-modal__count sa-coverage-modal__count--${tone}`;
  return <span className={className}>{n}</span>;
};

export default function HighCostApprovalsByLocationModal({
  isOpen,
  onClose,
  rows = [],
}) {
  const [sortColumn, setSortColumn] = useState('pending_count');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedLocationId, setExpandedLocationId] = useState(null);

  useEffect(() => {
    if (!isOpen) setExpandedLocationId(null);
  }, [isOpen]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'location' ? 'asc' : 'desc');
  };

  const renderSortIndicator = (column) => {
    if (sortColumn !== column) {
      return <span className="sa-coverage-modal__sort sa-coverage-modal__sort--idle">⇅</span>;
    }
    return (
      <span className="sa-coverage-modal__sort">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const sortedRows = useMemo(() => {
    if (!rows.length) return [];
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortColumn === 'location') {
        return direction * String(a.location_name || '').localeCompare(String(b.location_name || ''));
      }
      if (NUMERIC_COLUMNS.has(sortColumn)) {
        return direction * (toNumber(a[sortColumn]) - toNumber(b[sortColumn]));
      }
      return 0;
    });
  }, [rows, sortColumn, sortDirection]);

  const totals = useMemo(() => rows.reduce((acc, row) => ({
    pending_count: acc.pending_count + toNumber(row.pending_count),
    total_value: acc.total_value + toNumber(row.total_value),
    oldest_days: Math.max(acc.oldest_days, toNumber(row.oldest_days)),
  }), { pending_count: 0, total_value: 0, oldest_days: 0 }), [rows]);

  const locationsWithPending = rows.filter((row) => toNumber(row.pending_count) > 0).length;
  const locationCount = rows.length;

  const sortHeader = (column, label, numeric = false) => (
    <th
      className={`sortable${numeric ? ' num' : ''}`}
      onClick={() => handleSort(column)}
    >
      {label}{renderSortIndicator(column)}
    </th>
  );

  const toggleLocation = (row) => {
    const key = row.location_id || row.location_name;
    setExpandedLocationId((current) => (current === key ? null : key));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="High-cost approvals by location" size="large">
      <div className="sa-coverage-modal">
        {locationsWithPending > 0 ? (
          <div className="sa-coverage-modal__banner sa-highcost-modal__banner">
            {locationsWithPending} of {locationCount} location{locationCount !== 1 ? 's' : ''} {locationsWithPending === 1 ? 'has' : 'have'} high-cost pending
          </div>
        ) : (
          <div className="sa-coverage-modal__meta">
            {locationCount} location{locationCount !== 1 ? 's' : ''} · no high-cost pending
          </div>
        )}
        <p className="sa-coverage-modal__hint">
          Devices above each location&apos;s high-cost threshold. Location admins action these.
          Select a location to see the devices.
        </p>

        {sortedRows.length === 0 ? (
          <p className="sa-coverage-modal__empty">No location data available.</p>
        ) : (
          <div className="sa-location-budget-table-wrap">
            <table className="sa-location-budget-table sa-coverage-modal__table">
              <thead>
                <tr>
                  {sortHeader('location', 'Location')}
                  {sortHeader('pending_count', 'Pending', true)}
                  {sortHeader('total_value', 'Value', true)}
                  {sortHeader('oldest_days', 'Oldest', true)}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => {
                  const key = row.location_id || row.location_name;
                  const expanded = expandedLocationId === key;
                  const devices = row.devices || [];
                  return (
                    <Fragment key={key}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className="sa-coverage-modal__location"
                            onClick={() => toggleLocation(row)}
                            aria-expanded={expanded}
                          >
                            {row.location_name}
                            {row.location_code && row.location_code !== 'N/A' && (
                              <span className="sa-coverage-modal__code">{row.location_code}</span>
                            )}
                          </button>
                        </td>
                        <td className="num">
                          <Count value={row.pending_count} tone="expiring" />
                        </td>
                        <td className="num">
                          {toNumber(row.pending_count) > 0
                            ? formatCurrencyDynamic(row.total_value)
                            : <span className="sa-coverage-modal__count sa-coverage-modal__count--zero">—</span>}
                        </td>
                        <td className="num">
                          {toNumber(row.oldest_days) > 0
                            ? `${toNumber(row.oldest_days)}d`
                            : <span className="sa-coverage-modal__count sa-coverage-modal__count--zero">—</span>}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="sa-highcost-modal__detail-row">
                          <td colSpan={4}>
                            {devices.length === 0 ? (
                              <p className="sa-highcost-modal__none">No high-cost devices pending at this location.</p>
                            ) : (
                              <ul className="sa-highcost-modal__devices">
                                {devices.map((device) => (
                                  <li key={device.id} className="sa-highcost-modal__device">
                                    <span className="sa-highcost-modal__device-name">
                                      {device.device_name}
                                      {device.device_identifier && (
                                        <span className="sa-highcost-modal__device-id">{device.device_identifier}</span>
                                      )}
                                    </span>
                                    <span className="sa-highcost-modal__device-cost">
                                      {formatCurrencyDynamic(device.product_cost)}
                                    </span>
                                    <span className="sa-highcost-modal__device-days">
                                      {toNumber(device.days_pending)}d pending
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>All locations</td>
                  <td className="num"><Count value={totals.pending_count} tone="expiring" /></td>
                  <td className="num">
                    {totals.pending_count > 0
                      ? formatCurrencyDynamic(totals.total_value)
                      : <span className="sa-coverage-modal__count sa-coverage-modal__count--zero">—</span>}
                  </td>
                  <td className="num">
                    {totals.oldest_days > 0
                      ? `${totals.oldest_days}d`
                      : <span className="sa-coverage-modal__count sa-coverage-modal__count--zero">—</span>}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
